import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Button,
  Checkbox,
  Chip,
  Avatar,
} from '@mui/material';
import { Sparkles, CheckCircle2 } from 'lucide-mui';
import meetingImage from './assets/meeting.webp';

// Mock files data
const mockFiles = [
  {
    _id: '1',
    name: 'Weekly Team Standup - March 15',
    createdAt: '2024-03-15T10:00:00Z',
    thumbUrl: meetingImage,
    duration: 1800, // 30 minutes
  },
  {
    _id: '2',
    name: 'Product Planning Session',
    createdAt: '2024-03-14T14:30:00Z',
    thumbUrl: meetingImage,
    duration: 2700, // 45 minutes
  },
  {
    _id: '3',
    name: 'Sprint Retrospective Meeting',
    createdAt: '2024-03-13T16:00:00Z',
    thumbUrl: meetingImage,
    duration: 2400, // 40 minutes
  },
];

const mockResponse = `# Common Themes Across 3 Recordings

## **Key Recurring Topics**

### 1. **Project Timeline Concerns**
Multiple team members across all recordings expressed concerns about the current project timeline being too aggressive.

**Mentioned in:**
- Weekly Team Standup (3 times)
- Product Planning Session (5 times) 
- Sprint Retrospective (2 times)

### 2. **Resource Allocation Issues**
Teams consistently discussed the need for additional development resources and better task distribution.

**Common Pain Points:**
- Frontend development bottleneck 
- UX design capacity constraints
- Testing phase timeline compression

### 3. **Communication Gaps**
Cross-functional communication challenges were highlighted in every meeting.

**Patterns Identified:**
- Client feedback delays
- Design-to-development handoff issues
- Stakeholder alignment problems

## **Action Items Across Meetings**

| Priority | Action Item | Frequency | Status |
|----------|-------------|-----------|---------|
| High | Reassess project timeline | 3/3 meetings | Pending |
| High | Hire additional developer | 3/3 meetings | In progress |
| Medium | Improve client communication | 3/3 meetings | Planned |
| Medium | Streamline design process | 2/3 meetings | Not started |

## **Sentiment Analysis**

**Overall Team Mood:** Cautiously optimistic but concerned about deliverables

**Confidence Levels:**
- Technical execution: High (85%)
- Timeline adherence: Low (45%) 
- Resource adequacy: Medium (60%)
- Client satisfaction: High (80%)

## **Recommendations**

1. **Immediate Focus:** Address timeline and resource concerns through stakeholder discussion
2. **Process Improvement:** Implement weekly cross-team sync meetings
3. **Risk Mitigation:** Create contingency plans for potential delays
4. **Communication:** Establish clear client feedback cycles

---
*Analysis based on 3 recordings totaling 2.75 hours of meeting content*`;

const MultiAskAIDemo: React.FC = () => {
  const theme = useTheme();
  const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
  const [demoPhase, setDemoPhase] = useState(1); // 1: file selection, 2: streaming, 3: complete
  const [streamingResponse, setStreamingResponse] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const streamingRef = useRef<number | null>(null);
  const currentIndexRef = useRef(0);

  // Auto-select files one at a time
  useEffect(() => {
    if (demoPhase === 1) {
      const selectFilesSequentially = () => {
        // Select first file after 1 second
        setTimeout(() => {
          setSelectedFiles(['1']);
        }, 1000);

        // Select second file after 2 seconds
        setTimeout(() => {
          setSelectedFiles(['1', '2']);
        }, 2000);

        // Select third file after 3 seconds
        setTimeout(() => {
          setSelectedFiles(['1', '2', '3']);
        }, 3000);
      };

      selectFilesSequentially();
    }
    return () => {}; // Return empty cleanup function when condition is not met
  }, [demoPhase]);

  // Start analysis when files are selected
  useEffect(() => {
    if (selectedFiles.length === 3 && demoPhase === 1) {
      const timer = setTimeout(() => {
        setDemoPhase(2);
      }, 1000);
      return () => clearTimeout(timer);
    }
    return () => {}; // Return empty cleanup function when condition is not met
  }, [selectedFiles, demoPhase]);

  // Demo sequence management
  useEffect(() => {
    let timer: number;

    switch (demoPhase) {
      case 2: {
        // Start streaming immediately when analysis starts
        if (!isStreaming) {
          setIsStreaming(true);
        }
        break;
      }
      case 3: {
        // Reset after showing complete response
        timer = window.setTimeout(() => {
          setSelectedFiles([]);
          setStreamingResponse('');
          setDemoPhase(1);
          setIsStreaming(false);
        }, 4000);
        break;
      }
      default:
        break;
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [demoPhase, isStreaming]);

  // Streaming text generation
  useEffect(() => {
    if (demoPhase === 2 && isStreaming) {
      setStreamingResponse('');
      currentIndexRef.current = 0;

      const streamText = () => {
        const words = mockResponse.split(' ');

        const addNextWord = () => {
          if (currentIndexRef.current < words.length) {
            const currentWords = words.slice(0, currentIndexRef.current + 1);
            setStreamingResponse(currentWords.join(' '));
            currentIndexRef.current += 1;

            // Fast streaming speed like TemplatesDemo
            const currentWord = words[currentIndexRef.current - 1];
            let delay = 8; // Base delay

            if (currentWord?.includes('\n\n')) {
              delay = 25; // Pause at paragraph breaks
            } else if (currentWord?.includes('\n')) {
              delay = 20; // Pause at line breaks
            } else if (
              currentWord?.includes('.') ||
              currentWord?.includes('!') ||
              currentWord?.includes('?')
            ) {
              delay = 15; // Pause at sentence endings
            } else if (
              currentWord?.includes(',') ||
              currentWord?.includes(':')
            ) {
              delay = 12; // Pause at commas/colons
            } else if (currentWord?.length > 8) {
              delay = 10; // Slightly slower for longer words
            }

            streamingRef.current = window.setTimeout(addNextWord, delay);
          } else {
            // Streaming complete
            setIsStreaming(false);
            setDemoPhase(3);
          }
        };

        addNextWord();
      };

      streamText();
    }

    return () => {
      if (streamingRef.current) {
        clearTimeout(streamingRef.current);
      }
    };
  }, [demoPhase, isStreaming]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDuration = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    return `${minutes}min`;
  };

  const handleFileSelect = (fileId: string) => {
    setSelectedFiles((prev) =>
      prev.includes(fileId)
        ? prev.filter((id) => id !== fileId)
        : [...prev, fileId]
    );
  };

  const renderFileList = () => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          Select Recordings
        </Typography>
        <Chip
          label={`${selectedFiles.length} selected`}
          size="small"
          color={selectedFiles.length > 0 ? 'primary' : 'default'}
          sx={{ fontSize: '0.75rem' }}
        />
      </Box>

      <List disablePadding>
        {mockFiles.map((file, index) => (
          <ListItem
            key={file._id}
            disablePadding
            sx={{
              borderBottom: index < mockFiles.length - 1 ? 1 : 0,
              borderColor: 'secondary.light',
              '&:last-child': {
                borderBottom: 0,
              },
            }}
          >
            <ListItemButton
              onClick={() => handleFileSelect(file._id)}
              sx={{
                py: 1.5,
                px: 2,
                transition: theme.transitions.create('background-color'),
                bgcolor: selectedFiles.includes(file._id)
                  ? theme.palette.action.hover
                  : 'transparent',
                '&:hover': {
                  bgcolor: theme.palette.action.hover,
                },
                height: 'auto',
                minHeight: '72px',
                cursor: 'pointer',
              }}
            >
              <Checkbox
                checked={selectedFiles.includes(file._id)}
                tabIndex={-1}
                disableRipple
                size="small"
                sx={{ mr: 1 }}
              />

              {/* Thumbnail */}
              <Avatar
                src={file.thumbUrl}
                variant="rounded"
                sx={{
                  width: 48,
                  height: 36,
                  mr: 1.5,
                  bgcolor: theme.palette.grey[200],
                }}
              />

              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: selectedFiles.includes(file._id) ? 600 : 400,
                      color: 'text.primary',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.2,
                      mb: 0.5,
                      whiteSpace: 'normal',
                      height: 'auto',
                      pr: 4,
                      fontSize: '0.875rem !important',
                    }}
                  >
                    {file.name}
                  </Typography>
                }
                secondary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem !important' }}
                    >
                      {formatDate(file.createdAt)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: '0.7rem !important' }}
                    >
                      • {formatDuration(file.duration)}
                    </Typography>
                  </Box>
                }
                primaryTypographyProps={{
                  variant: 'body2',
                  fontWeight: selectedFiles.includes(file._id) ? 500 : 400,
                  color: 'text.primary',
                  sx: {
                    fontSize: '0.875rem !important',
                  },
                }}
                secondaryTypographyProps={{
                  variant: 'caption',
                  color: 'text.secondary',
                  sx: {
                    fontSize: '0.7rem !important',
                  },
                }}
                sx={{
                  my: 0,
                  overflow: 'hidden',
                  width: '100%',
                  pr: 1,
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {selectedFiles.length > 0 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="contained"
            startIcon={<CheckCircle2 style={{ width: 16, height: 16 }} />}
            sx={{
              borderRadius: 6,
              textTransform: 'none',
              px: 3,
            }}
          >
            Find Commonalities
          </Button>
        </Box>
      )}
    </Box>
  );

  const renderStreamingResponse = () => (
    <Box sx={{ position: 'relative', minHeight: 300 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}
            >
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: 600, mb: 1.5, mt: 2, color: 'text.primary' }}
            >
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography
              variant="subtitle1"
              component="h3"
              sx={{ fontWeight: 600, mb: 1, mt: 1.5, color: 'text.primary' }}
            >
              {children}
            </Typography>
          ),
          p: ({ children }) => (
            <Typography
              variant="body2"
              sx={{ mb: 1.5, lineHeight: 1.6, color: 'text.secondary' }}
            >
              {children}
            </Typography>
          ),
          ul: ({ children }) => (
            <Box
              component="ul"
              sx={{ mb: 1.5, pl: 2, color: 'text.secondary' }}
            >
              {children}
            </Box>
          ),
          li: ({ children }) => (
            <Box component="li" sx={{ mb: 0.5 }}>
              <Typography variant="body2" component="span">
                {children}
              </Typography>
            </Box>
          ),
          table: ({ children }) => (
            <Box
              component="table"
              sx={{
                width: '100%',
                borderCollapse: 'collapse',
                mb: 2,
                fontSize: '0.875rem',
                '& th, & td': {
                  border: `1px solid ${theme.palette.divider}`,
                  padding: '6px 8px',
                  textAlign: 'left',
                },
                '& th': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.05),
                  fontWeight: 600,
                },
              }}
            >
              {children}
            </Box>
          ),
          strong: ({ children }) => (
            <Box component="strong" sx={{ fontWeight: 600 }}>
              {children}
            </Box>
          ),
        }}
      >
        {streamingResponse}
      </ReactMarkdown>

      {/* Streaming cursor */}
      {isStreaming && (
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            width: '2px',
            height: '1.2em',
            backgroundColor: 'primary.main',
            ml: 0.5,
            animation: 'blink 1s infinite',
            '@keyframes blink': {
              '0%, 50%': { opacity: 1 },
              '51%, 100%': { opacity: 0 },
            },
          }}
        />
      )}
    </Box>
  );

  return (
    <Box
      sx={{
        p: 3,
        minHeight: 500,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Sparkles sx={{ fontSize: 20 }} />
          Multi Recording Ask AI
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Find patterns and insights across multiple recordings
        </Typography>
      </Box>

      {/* Content based on demo phase */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {demoPhase === 1 && renderFileList()}

        {(demoPhase === 2 || demoPhase === 3) && renderStreamingResponse()}
      </Box>
    </Box>
  );
};

export default MultiAskAIDemo;
